if (!customElements.get('wheelparts-carousel')) {
  customElements.define(
    'wheelparts-carousel',
    class WheelpartsCarousel extends HTMLElement {
      connectedCallback() {
        this.initialize();
      }

      initialize() {
        this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
        this.stage = this.querySelector('[data-carousel-stage]');
        this.current = Math.max(0, this.slides.findIndex((slide) => slide.dataset.initial === 'true'));
        this.dragStartX = null;
        this.dragCurrentX = null;
        this.activePointerId = null;
        this.suppressClick = false;

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        const options = { signal: this.abortController.signal };

        this.addEventListener('click', (event) => this.handleClick(event), options);
        this.addEventListener('keydown', (event) => this.handleKeydown(event), options);

        if (window.PointerEvent) {
          this.stage?.addEventListener('pointerdown', (event) => this.startDrag(event.clientX, event.pointerId, event), options);
          this.stage?.addEventListener('pointermove', (event) => this.updateDrag(event.clientX, event.pointerId), options);
          this.stage?.addEventListener('pointerup', (event) => this.endDrag(event.clientX, event.pointerId), options);
          this.stage?.addEventListener('pointercancel', () => this.cancelDrag(), options);
        } else {
          this.stage?.addEventListener('touchstart', (event) => this.startDrag(event.touches[0].clientX, 'touch', event), { ...options, passive: true });
          this.stage?.addEventListener('touchmove', (event) => this.updateDrag(event.touches[0].clientX, 'touch'), { ...options, passive: true });
          this.stage?.addEventListener('touchend', (event) => this.endDrag(event.changedTouches[0].clientX, 'touch'), options);
          this.stage?.addEventListener('mousedown', (event) => this.startDrag(event.clientX, 'mouse', event), options);
          window.addEventListener('mousemove', (event) => this.updateDrag(event.clientX, 'mouse'), options);
          window.addEventListener('mouseup', (event) => this.endDrag(event.clientX, 'mouse'), options);
        }

        this.slides.forEach((slide) => {
          slide.draggable = false;
          slide.querySelectorAll('img').forEach((image) => {
            image.draggable = false;
          });
        });

        this.render();
      }

      handleClick(event) {
        const previousButton = event.target.closest('[data-carousel-prev]');
        const nextButton = event.target.closest('[data-carousel-next]');
        const dot = event.target.closest('[data-carousel-dot]');
        const slide = event.target.closest('[data-carousel-slide]');

        if (previousButton) {
          event.preventDefault();
          event.stopPropagation();
          this.move(-1);
          return;
        }

        if (nextButton) {
          event.preventDefault();
          event.stopPropagation();
          this.move(1);
          return;
        }

        if (dot) {
          event.preventDefault();
          this.goTo(this.dots.indexOf(dot));
          return;
        }

        if (!slide) return;

        if (this.suppressClick) {
          event.preventDefault();
          this.suppressClick = false;
          return;
        }

        const index = this.slides.indexOf(slide);
        if (index !== this.current) {
          event.preventDefault();
          this.goTo(index);
        }
      }

      handleKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(1);
        }
      }

      startDrag(clientX, pointerId, event) {
        if (event.target.closest('button')) return;
        this.dragStartX = clientX;
        this.dragCurrentX = clientX;
        this.activePointerId = pointerId;
        this.classList.add('is-dragging');

        if (pointerId !== 'touch' && pointerId !== 'mouse') {
          try {
            this.stage?.setPointerCapture?.(pointerId);
          } catch (error) {
            // Synthetic events used by theme previews do not always have a capturable pointer.
          }
        }

        if (event.cancelable && event.pointerType === 'mouse') event.preventDefault();
      }

      updateDrag(clientX, pointerId) {
        if (this.dragStartX === null || pointerId !== this.activePointerId) return;
        this.dragCurrentX = clientX;
      }

      endDrag(clientX, pointerId) {
        if (this.dragStartX === null || pointerId !== this.activePointerId) return;
        const distance = clientX - this.dragStartX;
        this.cancelDrag();

        if (Math.abs(distance) >= 35) {
          this.suppressClick = true;
          this.move(distance < 0 ? 1 : -1);
          window.setTimeout(() => {
            this.suppressClick = false;
          }, 350);
        }
      }

      cancelDrag() {
        this.dragStartX = null;
        this.dragCurrentX = null;
        this.activePointerId = null;
        this.classList.remove('is-dragging');
      }

      move(direction) {
        if (this.slides.length < 2) return;
        this.current = (this.current + direction + this.slides.length) % this.slides.length;
        this.render();
      }

      goTo(index) {
        if (index < 0 || index >= this.slides.length) return;
        this.current = index;
        this.render();
      }

      render() {
        const total = this.slides.length;
        this.slides.forEach((slide, index) => {
          let position = index - this.current;
          if (position > total / 2) position -= total;
          if (position < -total / 2) position += total;

          const visiblePosition = Math.max(-2, Math.min(2, position));
          slide.dataset.position = String(visiblePosition);
          slide.dataset.hidden = Math.abs(position) > 2 ? 'true' : 'false';
          slide.setAttribute('aria-hidden', index === this.current ? 'false' : 'true');
          slide.tabIndex = index === this.current ? 0 : -1;
        });

        this.dots.forEach((dot, index) => {
          dot.setAttribute('aria-current', index === this.current ? 'true' : 'false');
        });
      }
    },
  );
}

if (!customElements.get('wheelparts-category-carousel')) {
  customElements.define(
    'wheelparts-category-carousel',
    class WheelpartsCategoryCarousel extends HTMLElement {
      connectedCallback() {
        this.initialize();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.abortController?.abort();
      }

      initialize() {
        this.viewport = this.querySelector('[data-category-viewport]');
        this.track = this.querySelector('[data-category-track]');
        this.cards = Array.from(this.querySelectorAll('.wheelparts-category'));
        this.previousButton = this.closest('.wheelparts-home__content')?.querySelector('[data-category-prev]');
        this.nextButton = this.closest('.wheelparts-home__content')?.querySelector('[data-category-next]');
        this.toggleButton = this.closest('.wheelparts-home__content')?.querySelector('[data-category-toggle]');
        this.autoplayEnabled = this.dataset.autoplay === 'true';
        this.autoplayPaused = false;
        this.speed = Math.max(3000, Number(this.dataset.speed) || 5000);
        this.currentIndex = 0;
        this.dragStartX = null;

        this.abortController?.abort();
        this.abortController = new AbortController();
        const options = { signal: this.abortController.signal };

        this.previousButton?.addEventListener('click', () => this.move(-1, true), options);
        this.nextButton?.addEventListener('click', () => this.move(1, true), options);
        this.toggleButton?.addEventListener('click', () => this.toggleAutoplay(), options);

        this.addEventListener('mouseenter', () => this.stopAutoplay(), options);
        this.addEventListener('mouseleave', () => this.startAutoplay(), options);
        this.addEventListener('focusin', () => this.stopAutoplay(), options);
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.startAutoplay();
        }, options);
        this.addEventListener('pointerdown', () => this.stopAutoplay(), options);
        this.addEventListener('pointerup', () => this.startAutoplay(), options);
        this.viewport?.addEventListener('pointerdown', (event) => {
          this.dragStartX = event.clientX;
        }, options);
        this.viewport?.addEventListener('pointerup', (event) => {
          if (this.dragStartX === null) return;
          const distance = event.clientX - this.dragStartX;
          this.dragStartX = null;
          if (Math.abs(distance) > 40) this.move(distance < 0 ? 1 : -1, true);
        }, options);
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.stopAutoplay();
          else this.startAutoplay();
        }, options);

        this.updateToggleButton();
        this.render();
        this.startAutoplay();
      }

      cardStep() {
        if (!this.cards.length) return 0;
        const first = this.cards[0];
        const track = first.parentElement;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        return first.getBoundingClientRect().width + gap;
      }

      move(direction, userInitiated = false) {
        if (!this.viewport || this.cards.length < 2) return;
        const visibleCards = Math.max(1, Math.round(this.viewport.clientWidth / this.cardStep()));
        const maxIndex = Math.max(0, this.cards.length - visibleCards);
        this.currentIndex += direction;
        if (this.currentIndex > maxIndex) this.currentIndex = 0;
        if (this.currentIndex < 0) this.currentIndex = maxIndex;
        this.render();

        if (userInitiated) this.restartAutoplay();
      }

      render() {
        if (!this.track) return;
        const offset = this.currentIndex * this.cardStep();
        this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      }

      goToCard(card) {
        const index = this.cards.indexOf(card);
        if (index < 0) return;
        const visibleCards = Math.max(1, Math.round(this.viewport.clientWidth / this.cardStep()));
        this.currentIndex = Math.min(index, Math.max(0, this.cards.length - visibleCards));
        this.render();
      }

      toggleAutoplay() {
        this.autoplayPaused = !this.autoplayPaused;
        this.updateToggleButton();
        if (this.autoplayPaused) this.stopAutoplay();
        else this.startAutoplay();
      }

      updateToggleButton() {
        if (!this.toggleButton) return;
        const paused = this.autoplayPaused || !this.autoplayEnabled;
        this.toggleButton.textContent = paused ? '▶' : 'Ⅱ';
        this.toggleButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
        this.toggleButton.setAttribute(
          'aria-label',
          paused ? this.toggleButton.dataset.playLabel : this.toggleButton.dataset.pauseLabel,
        );
      }

      startAutoplay() {
        if (
          !this.autoplayEnabled ||
          this.autoplayPaused ||
          this.cards.length < 2 ||
          document.hidden ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) return;
        this.stopAutoplay();
        this.autoplayTimer = window.setInterval(() => this.move(1), this.speed);
      }

      stopAutoplay() {
        if (this.autoplayTimer) window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      restartAutoplay() {
        this.stopAutoplay();
        this.startAutoplay();
      }
    },
  );
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('wheelparts-carousel').forEach((carousel) => carousel.initialize());
  event.target.querySelectorAll('wheelparts-category-carousel').forEach((carousel) => carousel.initialize());
});

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest?.('[data-carousel-slide]');
  const carousel = slide?.closest('wheelparts-carousel');
  if (slide && carousel) carousel.goTo(carousel.slides.indexOf(slide));

  const category = event.target.closest?.('.wheelparts-category');
  const categoryCarousel = category?.closest('wheelparts-category-carousel');
  if (category && categoryCarousel) categoryCarousel.goToCard(category);
});
